import { describeAccount, readAccount } from './account'

/**
 * Which account the assistant was signed in to, and on what plan.
 *
 * Absent when the agent read nothing, rather than present and empty. An account panel showing dashes
 * would be read as "we looked and there is nothing there", which is a different fact from "no policy
 * asked" — and both are different from a plan the agent actually resolved.
 *
 * When the read failed, this says so in words rather than showing a plan. That is the one behavior
 * the whole feature turns on: a machine whose sidebar was collapsed must never appear as being on the
 * free plan, because somebody would act on it.
 */
export function AccountSummary({ attributesJson }: { attributesJson: string | null | undefined }) {
  const account = readAccount(attributesJson)
  if (!account) return null

  const resolved = account.tier !== null

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>AI account</h3>

        <dl className="detail" data-testid="account-summary">
          <dt>Plan</dt>
          <dd data-testid="account-plan">
            {resolved ? (
              <span className="pill sev-4">{describeAccount(account)}</span>
            ) : (
              <span className="muted">{describeAccount(account)}</span>
            )}
          </dd>

          {account.userName && (
            <>
              <dt>Signed in as</dt>
              <dd data-testid="account-user">{account.userName}</dd>
            </>
          )}

          {account.ruleName && (
            <>
              <dt>Reported under</dt>
              <dd className="muted">{account.ruleName}</dd>
            </>
          )}
        </dl>

        {!resolved && (
          <div className="hint">
            The agent looked at the assistant's account row and did not come away with a plan. That is
            recorded as it happened rather than guessed at — a collapsed sidebar, a signed-out window,
            or a vendor that moved the control all read the same way from here, and none of them mean
            the free plan.
          </div>
        )}
      </div>
    </div>
  )
}
